import { OverlayTrigger, Popover } from 'react-bootstrap'
import { useId } from 'react'
import { IconPaths, KTSVG } from '_metronic/helpers'

/** Uma nota metodológica com o id que o card usa no `aria-describedby`. */
export type NotaAjuda = {
  id: string
  texto: string
}

type Props = {
  /** Nome acessível do botão — diz de qual indicador é a explicação. */
  rotulo: string
  notas: NotaAjuda[]
}

/** Descarta notas vazias e dá a cada uma um id estável, derivado do card. */
export const montarNotasAjuda = (idBase: string, notas: (string | undefined)[]): NotaAjuda[] =>
  notas
    .filter((nota): nota is string => Boolean(nota && nota.trim()))
    .map((texto, indice) => ({ id: `${idBase}-nota-${indice}`, texto }))

/**
 * Ícone `?` que guarda as notas metodológicas fora da área principal do card.
 *
 * O texto sai da tela, não da página: continua no DOM em `visually-hidden`, com o
 * mesmo id que o card aponta no `aria-describedby` — leitor de tela segue lendo a
 * regra do cálculo junto com o indicador. O botão é focável e abre o popover por
 * hover **e** por foco, então teclado e toque chegam na explicação (o
 * `InfoTooltip` genérico é um `span` só de hover).
 */
export const AjudaIndicador = ({ rotulo, notas }: Props) => {
  const idPopover = useId()

  if (notas.length === 0) return null

  return (
    <>
      <OverlayTrigger
        placement='auto'
        trigger={['hover', 'focus']}
        overlay={
          <Popover id={idPopover}>
            <Popover.Body className='fs-7 text-gray-800'>
              {notas.map((nota, indice) => (
                <p key={nota.id} className={indice < notas.length - 1 ? 'mb-2' : 'mb-0'}>
                  {nota.texto}
                </p>
              ))}
            </Popover.Body>
          </Popover>
        }
      >
        <button
          type='button'
          className='btn btn-icon btn-sm btn-active-light-primary w-25px h-25px flex-shrink-0'
          aria-label={rotulo}
        >
          <KTSVG path={IconPaths.Help} className='svg-icon-em fs-5 text-gray-700' ariaHidden />
        </button>
      </OverlayTrigger>

      {notas.map((nota) => (
        <span key={nota.id} id={nota.id} className='visually-hidden'>
          {nota.texto}
        </span>
      ))}
    </>
  )
}
